import { ListTodo, Video, AlertTriangle } from 'lucide-react';
import type { PositionedEvent } from '../utils';
import type { TimeFormat } from '../types';
import { formatTime, getEventColor, detectVideoLink } from '../utils';

interface TimeGridEventProps {
  positioned: PositionedEvent;
  onClick: () => void;
  timeFormat?: TimeFormat;
  resizingHeight?: number | null;
  onResizeStart?: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  hasConflict?: boolean;
}

export function TimeGridEvent({
  positioned,
  onClick,
  timeFormat = '12h',
  resizingHeight,
  onResizeStart,
  dimPastEvents = false,
  eventColorPriority = 'event',
  hasConflict = false,
}: TimeGridEventProps) {
  const { event, top, height: originalHeight, left, width } = positioned;
  const height = resizingHeight ?? originalHeight;
  const color = getEventColor(event, eventColorPriority);
  const isPast = dimPastEvents && new Date(event.endAt) < new Date();
  const metadata = (event.metadata || {}) as Record<string, unknown>;
  const isFocusTime = metadata.focusTime === true;
  const isScheduledTask = typeof metadata.schedulableTaskId === 'string';
  const hasVideoCall = !!detectVideoLink(event);

  return (
    <button
      type="button"
      className={`absolute rounded px-1 py-0.5 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity text-white text-[11px] leading-tight text-left group ${isPast ? 'opacity-50' : ''}`}
      style={{
        top: `${top}px`,
        height: `${Math.max(height, 18)}px`,
        left: `${left}%`,
        width: `calc(${width}% - 2px)`,
        backgroundColor: isFocusTime ? 'transparent' : color,
        ...(isFocusTime
          ? {
              border: `2px dashed ${color}`,
              backgroundImage: `repeating-linear-gradient(
                135deg,
                ${color}15,
                ${color}15 4px,
                ${color}08 4px,
                ${color}08 8px
              )`,
              color,
            }
          : {}),
        ...(hasConflict && !isFocusTime ? { borderLeft: '3px solid #ef4444' } : {}),
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={`${event.title}\n${formatTime(new Date(event.startAt), timeFormat)} – ${formatTime(new Date(event.endAt), timeFormat)}`}
    >
      <div className="font-medium truncate flex items-center gap-0.5">
        {hasConflict && <AlertTriangle className="h-3 w-3 shrink-0 text-amber-200" />}
        {isScheduledTask && <ListTodo className="h-3 w-3 shrink-0" />}
        {hasVideoCall && <Video className="h-3 w-3 shrink-0" />}
        {event.title}
      </div>
      {height >= 36 && (
        <div className="text-[10px] opacity-90 truncate">
          {formatTime(new Date(event.startAt), timeFormat)} –{' '}
          {formatTime(new Date(event.endAt), timeFormat)}
        </div>
      )}
      {height >= 54 && event.location && (
        <div className="text-[10px] opacity-80 truncate">{event.location}</div>
      )}

      {onResizeStart && (
        <div
          className="absolute bottom-0 left-0 right-0 h-2 cursor-s-resize opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
          onMouseDown={(e) => onResizeStart(event.eventId, height, e)}
        />
      )}
    </button>
  );
}
